import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { testimonials } from '@/mocks/articles';

export default function About() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const payload = new URLSearchParams();
    payload.append('name', formData.get('name') as string);
    payload.append('email', formData.get('email') as string);
    payload.append('message', formData.get('message') as string);

    try {
      const res = await fetch('https://readdy.ai/api/form/d9s94a7nahb7382ovsdg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const text = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || text || '';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormError('发送失败，请稍后再试。');
        } else {
          setFormError(serverMsg || '发送失败，请稍后再试。');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的网络连接后重试。');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '穿搭换装指南',
    url: window.location.origin,
    description: '围绕日常换装、穿搭搭配、脱衣顺序与收纳整理全流程，帮助爱美女性快速掌握高效换装方法。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '每天换衣服应该遵循什么顺序？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '建议遵循"由外向内、由上至下"的原则。穿衣时先内后外、先上后下；脱衣时先外后内、先上后下。',
        },
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), path: '/' },
              { label: t('breadcrumb.about') },
            ]}
          />
        </div>

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-14 md:pb-20">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-4">{t('about.title')}</h1>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              {t('site.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
            <div>
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-5 flex items-center gap-2">
                <i className="ri-book-open-line text-primary-500"></i>
                {t('about.story')}
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  穿搭换装指南诞生于一个简单的想法：让每一位女性都能在换装这件事上找到自信与从容。
                  我们的创始团队由资深时尚编辑、形象顾问和收纳整理师组成，多年来深耕女性穿搭与生活方式领域，
                  积累了丰富的实战经验和专业知识。
                </p>
                <p>
                  我们发现，很多女性在换装过程中遇到的困扰——衣柜乱成一团、不知道穿什么、
                  换衣服手忙脚乱——其实都可以通过科学的方法和技巧来解决。
                  正是基于这样的洞察，我们创建了这个平台，希望用通俗易懂的方式，
                  将专业的穿搭知识和换装技巧传递给每一位爱美女性。
                </p>
                <p>
                  从日常换装到旅行打包，从职场穿搭到约会造型，从脱衣顺序到衣物收纳，
                  我们致力于提供全方位、一站式的穿搭解决方案，让"穿什么"和"怎么穿"不再是难题。
                </p>
              </div>
            </div>

            <div className="bg-background-100/80 rounded-xl p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 flex items-center gap-2">
                <i className="ri-heart-line text-accent-500"></i>
                {t('about.reputation')}
              </h2>
              <div className="flex flex-col gap-5">
                {testimonials.map((item, idx) => (
                  <div key={idx} className="bg-background-50 rounded-lg p-4 md:p-5 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{item.name}</p>
                        <p className="text-xs text-foreground-500">{item.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-background-100/80 rounded-xl p-6 md:p-10">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center flex items-center justify-center gap-2">
                <i className="ri-mail-line text-primary-500"></i>
                {t('about.contact')}
              </h2>

              <form onSubmit={handleSubmit} data-readdy-form="true" className="flex flex-col gap-4">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-honeypot"
                />
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('contact.name')}
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('contact.email')}
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t('contact.message')}
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                </div>

                {formStatus === 'success' && (
                  <div className="bg-secondary-100 text-secondary-700 text-sm p-3 rounded-md text-center">
                    {t('contact.success')}
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="bg-primary-100 text-primary-700 text-sm p-3 rounded-md text-center">
                    {formError || t('contact.error')}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full bg-primary-500 text-background-50 px-6 py-3 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {formStatus === 'loading' ? '发送中...' : t('contact.submit')}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}